import { motion } from 'motion/react';

interface WhySwitchProps {
  competitorName: string;
  switchReasons: string[];
  prosVsPollenate: string[];
  consVsPollenate: string[];
}

const icons = ['⚡', '🎯', '💰', '🛠️', '🤖', '🌍'];

export function WhySwitch({ competitorName, switchReasons, prosVsPollenate, consVsPollenate }: WhySwitchProps) {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Why Switch */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Why Teams Switch to Pollenate
          </h2>
          <p className="text-lg text-slate-600 dark:text-neutral-400">
            Here&apos;s what makes Pollenate a better choice for feedback collection.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {switchReasons.map((reason, i) => (
            <motion.div
              key={reason}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5 hover:border-amber-200 dark:hover:border-amber-700/50 transition-colors"
            >
              <span className="text-2xl mb-3 block">{icons[i % icons.length]}</span>
              <p className="text-sm text-slate-700 dark:text-neutral-300 leading-relaxed">{reason}</p>
            </motion.div>
          ))}
        </div>

        {/* Honest comparison */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* What Pollenate does better */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 text-sm">✓</span>
              Where Pollenate Wins
            </h3>
            <ul className="space-y-3">
              {consVsPollenate.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600 dark:text-neutral-400">
                  <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* What competitor does better */}
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-sm">★</span>
              Where {competitorName} Wins
            </h3>
            <ul className="space-y-3">
              {prosVsPollenate.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600 dark:text-neutral-400">
                  <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
